import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { observer } from 'mobx-react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import * as Sentry from '@sentry/browser';

import { FeatureFlag, useClientHighlightSharingEnabled } from '../../../utils/postHog';

import StatAccordion from '../../../components/Accordion';
import AddToNotesMenu from '../../../components/AddToNotesMenu';
import User from '../../../Model/User';
import { DateFormat } from '../../../utils/date';
import ImageSlider from '../ImageSlider';
import useComponentMounted from '../../../hooks/useComponentMounted';
import useSessionStore from '../../../hooks/useSessionStore';
import useContentSharingConsent from '../../../hooks/useContentSharingConsent';
import ShareAsHighlightModal from '../../../chat/components/ShareAsHighlightModal';
import useToast from '../../hooks/useToast';
import UserNutritionMacroGoals from '../../Model/UserNutritionMacroGoals';
import AppCustomCheckin, { DEFAULT_TAG } from '../../Model/AppCustomCheckin';
import UserContext from '../../../context/UserContext';
import NutritionGoalsEditorModal from
'../../pages/MealsPlanner/MealPlanAssignment/components/NutritionGoalsEditorModal';

import CircumferenceStatTable from './CircumferenceStatTable';
import HabitStat from './HabitStat/HabitStat';
import CalorieIntakeChart from './CalorieIntakeChart';
import WorkoutStat from './WorkoutStat/WorkoutStat';
import CycleStat, { useCycleData } from './CycleStat';

import {
  Container,
  DataContainer,
  ImageContainer,
  StyledContainer,
  StatContainer,
  WeightStatContainer,
  WeightStatTitle,
  WeightStatValue,
  StatLabel,
  Stat,
  DifferenceStatLabel,
  DifferenceStatValue,
  CheckInQuestionContainer,
  QuestionText,
  AnswerText,
  StyledTitle,
  WeightStat,
  StyledIconButton,
  QuestionRow,
  AnswerActionButton,
  StyledHighlightIcon,
  StyledChartIcon,
  StyledEditIcon,
} from './styles';
import texts from './texts.json';

import {
  checkInColumns,
  measurement,
  getMeasurementDifference,
} from './utils';

const CheckInStats = ({
  activity,
  isNotification,
  title,
  onWeightHistoryClick,
  clientUserDoc,
}) => {
  const [isGoalsModalOpen, setIsGoalsModalOpen] = useState(false);
  const [macroGoalsDoc, setMacroGoalsDoc] = useState(null);
  // null = coach question order not fetched yet; array once loaded (empty if no default template).
  const [orderedQuestionIds, setOrderedQuestionIds] = useState(null);
  const [highlightPhotoPair, setHighlightPhotoPair] = useState(null);
  const [highlightAnswerSource, setHighlightAnswerSource] = useState(null);
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const { isAdmin, isCoachAssistant } = useSessionStore();
  const isCoachTasksEnabled = useFeatureFlagEnabled(FeatureFlag.ENABLE_COACH_TASKS);
  // The coach who owns this client, from the /c/:userId route - NOT the logged-in user
  // (which differs when an admin / on-call user views another coach's client).
  const { userId: coachId } = useContext(UserContext);
  const isHighlightSharingEnabled = useClientHighlightSharingEnabled(coachId);

  const userId = activity?.user;
  /*
    Only staff behind both flags can ever act on a highlight, so only they need the answer — anyone else
    would pay a `postPaymentFormAnswer` read per client in their feed for actions they never see.
  */
  const canUseHighlightSharing = (isAdmin || isCoachAssistant) && isCoachTasksEnabled && isHighlightSharingEnabled;
  const {
    isAllowed: isConsentAllowed,
    requiresClientApproval,
    isReady: isConsentReady,
  } = useContentSharingConsent(
    canUseHighlightSharing ? userId : null,
  );

  useEffect(() => {
    const fetchMacroGoals = async () => {
      if (userId) {
        const doc = await UserNutritionMacroGoals.getById(userId);
        if (isComponentMountedRef.current) {
          setMacroGoalsDoc(doc);
        }
      }
    };
    fetchMacroGoals();
  }, [userId, isComponentMountedRef]);

  // Cycle context for female clients who track their cycle. Every caller passes the client's user doc (the feed card
  // once it has loaded), so nothing extra is read for it here.
  const currentSubmittedAt = activity.checkInData?.current?.submittedAt?.toDate?.() || null;
  const cycleData = useCycleData({
    userId,
    isCycleTrackingActive: !!clientUserDoc?.isCycleTrackingActive,
    windowEnd: currentSubmittedAt,
  });

  // Feed cards (CoachActivity) carry no `questions`, so there is nothing to order there.
  const hasCheckInQuestions = !!activity.questions;

  // The check-in doc's `questions` map carries no ordering, and Firestore returns map
  // keys in a fixed lexicographic order - not the order the coach configured. Fetch the
  // coach's default check-in template directly (a fast, single-collection query, unlike
  // CustomizationContext which is deferred behind ManageProgramContext/WorkoutContext)
  // to get the intended question order. Keyed only on the coach, so navigating between
  // a client's check-ins does not refetch.
  useEffect(() => {
    const fetchQuestionOrder = async () => {
      if (coachId && hasCheckInQuestions) {
        const checkInCollection = await AppCustomCheckin.getCheckinDocsByCoachId(coachId);
        const defaultCheckInDoc = checkInCollection.docs.find((doc) => doc.tag === DEFAULT_TAG);
        if (isComponentMountedRef.current) {
          setOrderedQuestionIds(defaultCheckInDoc?.checkInQuestions || []);
        }
      }
    };
    fetchQuestionOrder();
  }, [coachId, hasCheckInQuestions, isComponentMountedRef]);

  // calculate difference of the value between current and last check-in
  // of given measurement type
  const getDifference = useCallback((measurementType, column) => {
    let differenceLabel = texts.emptyCell;
    let isPositive = false;

    if (activity.checkInData.current
      && activity.checkInData[column]
      && activity.checkInData.current[measurementType]?.value
      && activity.checkInData[column][measurementType]?.value) {
      const difference = getMeasurementDifference(
        activity.checkInData.current,
        activity.checkInData[column],
        measurementType,
      );
      isPositive = difference > 0;
      differenceLabel = `${isPositive ? texts.plus : ''}${difference % 1 ? difference.toFixed(2) : difference}
        ${activity.checkInData.current[measurementType].unit}`;
    }
    return {
      differenceLabel,
      isPositive,
    };
  }, [
    activity,
  ]);

  // render difference value for given measurement type with positive and negative
  // styled in green and blue respectively
  const renderDifferenceStat = useCallback((measurementType, column = checkInColumns[1]) => {
    const { isPositive, differenceLabel } = getDifference(measurementType, column);
    return (
      <DifferenceStatValue $isPositive={isPositive}>
        {differenceLabel}
      </DifferenceStatValue>
    );
  }, [
    getDifference,
  ]);

  const shouldShowQuestions = (activity.questions
    && Object.values(activity.questions).some((questionObject) => questionObject.value))
    || activity.checkInData?.current.win
    || activity.checkInData?.current.status
    || activity.checkInData?.current.comment;

  const checkInQuestionAnswers = useMemo(() => {
    // Wait for the coach's question order before rendering answers. Returning [] until
    // then means answers appear once, already in order (a load) rather than rendering
    // in an arbitrary order and then visibly re-sorting (a reorder).
    if (!activity.questions || orderedQuestionIds === null) {
      return [];
    }

    const seenQuestionIds = new Set();
    const orderedAnswers = orderedQuestionIds.reduce((acc, question) => {
      const questionObject = activity.questions[question];
      if (questionObject && questionObject.value) {
        acc.push(questionObject);
        seenQuestionIds.add(question);
      }
      return acc;
    }, []);

    // Append any answered questions missing from the coach's ordered list (e.g. a
    // question removed from the template after this check-in was submitted) so answers
    // are never hidden.
    return Object.keys(activity.questions).reduce((acc, question) => {
      const questionObject = activity.questions[question];
      if (questionObject && questionObject.value && !seenQuestionIds.has(question)) {
        acc.push(questionObject);
      }
      return acc;
    }, orderedAnswers);
  }, [
    activity,
    orderedQuestionIds,
  ]);

  const handleSaveNutritionGoals = useCallback(async (docContent) => {
    if (macroGoalsDoc) {
      try {
        await macroGoalsDoc.saveHistory('', '', {});
        await macroGoalsDoc.updateFields(docContent);
        showToast(texts.updateSuccess);
        setIsGoalsModalOpen(false);
      } catch (error) {
        Sentry.captureException(error);
        showToast(texts.updateError, { error: true });
      }
    }
  }, [macroGoalsDoc, showToast]);

  const mealPlanPercentages = activity?.mealsSummary?.mealPlan?.macrosPercentages || {};
  const mealPlanMacroAverages = {
    percentages: mealPlanPercentages,
    macros: {},
  };

  const macroAverages = macroGoalsDoc?.macroAverages || {};
  const currentMacroAverages = {
    percentages: macroAverages.percentages || mealPlanMacroAverages.percentages,
    macros: macroAverages.macros || {},
  };

  const checkInId = activity?.checkInId || activity?.id;

  const handleCreateHighlight = useCallback((index) => {
    const { first, current } = activity.checkInData || {};
    const submittedAt = (checkInData) => (checkInData?.submittedAt
      ? checkInData.submittedAt.toDate().toISOString()
      : undefined);

    const afterUrl = current?.attachments?.[index];
    if (!afterUrl) {
      showToast(texts.highlightPhotoMissing, { error: true });
      return;
    }

    const beforeUrl = first?.attachments?.[index];

    setHighlightPhotoPair({
      checkInId,
      attachmentIndex: index,
      // Same URL means the selection is the first check-in, so there is nothing to compare against.
      ...(beforeUrl && beforeUrl !== afterUrl ? { before: { url: beforeUrl, at: submittedAt(first) } } : {}),
      after: { url: afterUrl, at: submittedAt(current) },
    });
  }, [activity, checkInId, showToast]);

  /*
    `questionKey` is the check-in question's own id where there is one. The win / status / comment rows are
    fixed fields on the check-in rather than configured questions, so they pass their field name — which is
    just as stable, and stable is all the key has to be for a highlight to say where its words came from.
  */
  const handleHighlightAnswer = useCallback((questionKey, question, text) => {
    setHighlightAnswerSource({
      checkInId, questionKey, question, text: String(text ?? ''),
    });
  }, [checkInId]);

  if (!activity.checkInData) {
    return null;
  }

  const getDateText = (checkInData) => {
    let dateText = '';
    if (checkInData && checkInData.submittedAt) {
      dateText = ` - ${moment(checkInData.submittedAt.toDate()).format(DateFormat.TEXT_DATE_FORMAT)}`;
    }
    return dateText;
  };

  const isHighlightEligible = canUseHighlightSharing && !!checkInId && !!userId;
  /*
    A client who asked to be asked can still be highlighted: the modal sends the photo to them for a yes
    instead of straight to the coach, so the action is offered and only its outcome differs.
  */
  const canSharePhoto = isConsentAllowed || requiresClientApproval;
  const canHighlightPhoto = isHighlightEligible && canSharePhoto;
  // Only once consent is known — otherwise a ✕ flashes on every check-in while the answer loads.
  const isBlockedByConsent = isHighlightEligible && isConsentReady && !canSharePhoto;

  const renderHighlightAnswerAction = (questionKey, question, value) => {
    if (!isHighlightEligible || !String(value ?? '').trim()) {
      return null;
    }
    return (
      <AnswerActionButton
        onClick={() => handleHighlightAnswer(questionKey, question, value)}
        title={texts.createHighlight}
      >
        <StyledHighlightIcon />
      </AnswerActionButton>
    );
  };

  return (
    <Container>
      <AddToNotesMenu clientUserDoc={clientUserDoc}>
        <StyledContainer>
          {title && <StyledTitle>{title}</StyledTitle>}
          {/* Weight comparison between start, last and latest check-in weights */}
          <StatContainer>
            <WeightStat $width="60%" showDivider>
              <WeightStatTitle>{texts.weightStat}</WeightStatTitle>
              <WeightStatContainer>
                {checkInColumns.map((column) => (
                  <Stat key={`${activity.id}_${column}`}>
                    <WeightStatValue $current={column === checkInColumns[2]}>
                      {(activity.checkInData[column] && activity.checkInData[column].weight.value
                        && `${activity.checkInData[column].weight.value} ${activity.checkInData[column].weight.unit}`)
                        || texts.emptyCell}
                    </WeightStatValue>
                    <StatLabel>{texts[column]}</StatLabel>
                  </Stat>
                ))}
              </WeightStatContainer>
              <StyledIconButton
                onClick={onWeightHistoryClick}
              >
                <StyledChartIcon />
              </StyledIconButton>
            </WeightStat>
            <WeightStat $width="40%">
              <WeightStatTitle>{texts.weightDifferenceLabel}</WeightStatTitle>
              <WeightStatContainer>
                <Stat>
                  {renderDifferenceStat(measurement.WEIGHT, checkInColumns[1])}
                  <DifferenceStatLabel>{texts.differenceLast}</DifferenceStatLabel>
                </Stat>
                <Stat>
                  {renderDifferenceStat(measurement.WEIGHT, checkInColumns[0])}
                  <DifferenceStatLabel>{texts.differenceStart}</DifferenceStatLabel>
                </Stat>
              </WeightStatContainer>
            </WeightStat>
          </StatContainer>
          {/* Comparison of last and current check-in image uploads */}
          <ImageContainer>
            <ImageSlider
              imageList={activity.checkInData.first.attachments}
              resizedImageList={activity.checkInData.first.resizedAttachments}
              label={`${texts.initialCheckIn}${getDateText(activity.checkInData.first)}`}
            />
            {!isNotification && (
              <ImageSlider
                imageList={activity.checkInData.last?.attachments}
                resizedImageList={activity.checkInData.last?.resizedAttachments}
                label={`${texts.last}${getDateText(activity.checkInData.last)}`}
              />
            )}
            <ImageSlider
              imageList={activity.checkInData.current.attachments}
              resizedImageList={activity.checkInData.current.resizedAttachments}
              label={texts.current}
              onImageAction={canHighlightPhoto ? handleCreateHighlight : null}
              imageActionLabel={texts.createHighlight}
              isActionBlocked={isBlockedByConsent}
              actionBlockedLabel={texts.highlightNoConsent}
            />
          </ImageContainer>
          {/* Answers to Check-in Questions */}
          <DataContainer>
            {shouldShowQuestions && (
              <StatAccordion
                title={texts.checkInQuestions}
                defaultExpanded={!isNotification}
              >
                {!!checkInQuestionAnswers && checkInQuestionAnswers.map((questionObject) => (
                  <CheckInQuestionContainer key={questionObject.id}>
                    <QuestionRow>
                      <QuestionText>{`${questionObject.question}: `}</QuestionText>
                      {renderHighlightAnswerAction(questionObject.id, questionObject.question, questionObject.value)}
                    </QuestionRow>
                    <AnswerText>{questionObject.value}</AnswerText>
                  </CheckInQuestionContainer>
                ))}
                {activity.checkInData.current.win && (
                  <CheckInQuestionContainer>
                    <QuestionRow>
                      <QuestionText>{texts.winQuestion}</QuestionText>
                      {renderHighlightAnswerAction('win', texts.winQuestion, activity.checkInData.current.win)}
                    </QuestionRow>
                    <AnswerText>{activity.checkInData.current.win}</AnswerText>
                  </CheckInQuestionContainer>
                )}
                {activity.checkInData.current.status && (
                  <CheckInQuestionContainer>
                    <QuestionRow>
                      <QuestionText>{texts.statusQuestion}</QuestionText>
                      {renderHighlightAnswerAction('status', texts.statusQuestion, activity.checkInData.current.status)}
                    </QuestionRow>
                    <AnswerText>{activity.checkInData.current.status}</AnswerText>
                  </CheckInQuestionContainer>
                )}
                {activity.checkInData.current.comment && (
                  <CheckInQuestionContainer>
                    <QuestionRow>
                      <QuestionText>{texts.commentQuestion}</QuestionText>
                      {renderHighlightAnswerAction(
                        'comment',
                        texts.commentQuestion,
                        activity.checkInData.current.comment,
                      )}
                    </QuestionRow>
                    <AnswerText>{activity.checkInData.current.comment}</AnswerText>
                  </CheckInQuestionContainer>
                )}
              </StatAccordion>
            )}
            {/* circumference measurement comparison accordion */}
            <StatAccordion
              title={texts.circumferenceStats}
              defaultExpanded={!isNotification}
            >
              <CircumferenceStatTable
                renderDifferenceStat={renderDifferenceStat}
                activity={activity}
              />
            </StatAccordion>
            {/* cycle phase and symptoms by week, opening on the check-in week (female clients tracking their cycle) */}
            {!!cycleData && (
              <StatAccordion
                title={texts.cycleSummary}
                defaultExpanded={!isNotification}
              >
                <CycleStat
                  key={cycleData.checkInDateId}
                  settings={cycleData.settings}
                  dayLogs={cycleData.dayLogs}
                  checkInDateId={cycleData.checkInDateId}
                  todayId={cycleData.todayId}
                />
              </StatAccordion>
            )}
            {/* calorie intake since last check-in */}
            {!!activity.userMealsSubmitted && (
              <StatAccordion
                title={texts.calorieSummary}
                defaultExpanded={!isNotification}
                actions={macroGoalsDoc && (
                  <StyledIconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      setIsGoalsModalOpen(true);
                    }}
                  >
                    <StyledEditIcon />
                  </StyledIconButton>
                )}
              >
                <CalorieIntakeChart activity={activity} />
              </StatAccordion>
            )}
            {/* habit since last check-in */}
            <StatAccordion
              title={texts.habitSummary}
              defaultExpanded={!isNotification}
            >
              <HabitStat activity={activity} />
            </StatAccordion>
            {/* workout stats since last check-in */}
            {activity.workoutData && (
              <StatAccordion
                title={texts.workoutsSummary}
                defaultExpanded={!isNotification}
              >
                <WorkoutStat activity={activity} clientAge={clientUserDoc?.age || null} />
              </StatAccordion>
            )}
          </DataContainer>
        </StyledContainer>
      </AddToNotesMenu>

      {macroGoalsDoc && (
        <NutritionGoalsEditorModal
          isOpen={isGoalsModalOpen}
          onClose={() => setIsGoalsModalOpen(false)}
          currentCalories={macroGoalsDoc.totalDailyCalories || 0}
          currentMacroAverages={currentMacroAverages}
          userNutritionMacroGoalsDoc={macroGoalsDoc}
          mealPlanMacroAverages={mealPlanMacroAverages}
          mealPlanId=""
          mealPlanName=""
          onSave={handleSaveNutritionGoals}
        />
      )}

      {!!highlightPhotoPair && (
        <ShareAsHighlightModal
          isOpen
          onClose={() => setHighlightPhotoPair(null)}
          userId={userId}
          viewAsCoachId={coachId}
          clientName={clientUserDoc?.name || ''}
          photoPair={highlightPhotoPair}
        />
      )}

      {!!highlightAnswerSource && (
        <ShareAsHighlightModal
          isOpen
          onClose={() => setHighlightAnswerSource(null)}
          userId={userId}
          viewAsCoachId={coachId}
          clientName={clientUserDoc?.name || ''}
          answerSource={highlightAnswerSource}
        />
      )}
    </Container>
  );
};

CheckInStats.propTypes = {
  activity: PropTypes.object,
  isNotification: PropTypes.bool,
  title: PropTypes.string,
  onWeightHistoryClick: PropTypes.func,
  clientUserDoc: PropTypes.instanceOf(User),
};

CheckInStats.defaultProps = {
  activity: {},
  isNotification: false,
  title: '',
  onWeightHistoryClick: () => {},
  clientUserDoc: null,
};

export default observer(CheckInStats);
